/**
 * Structure behind AssistantTravelPlannerPage: ids, regions, image files, tile and pop-up layout.
 * Every visible string lives in the `assistantTravelPlanner` i18n namespace, keyed by the ids below.
 *
 * Images are served from /public (frontend/public/img/assistant-travel-planner) and referenced by path,
 * not imported: ~190 files are named from the arrays in this module, which would be 190 import lines.
 */

export type Assistant = "chatgpt" | "claude";

export const ASSISTANT_NAMES: Record<Assistant, string> = { chatgpt: "ChatGPT", claude: "Claude" };

export const ASSET_BASE = "/img/assistant-travel-planner";
/** Path relative to the asset root, as in the designer's HTML (`photos/thailand.jpg`). */
export const asset = (relative: string) => `${ASSET_BASE}/${relative}`;

// The homepage's hero clip (frontend/public). The designer's copy of it is byte-identical.
export const HERO_VIDEO_WEBM = "/hero-s-v1.webm";
export const HERO_VIDEO_MP4 = "/hero-s-v1.mp4";
export const HERO_VIDEO_POSTER = "/hero-s-v1-poster.jpg";

export const MCP_SERVER_URL = "https://www.stippl.io/api/mcp";
/**
 * Stippl's listing in Claude's connector directory (live since 2026-10-13): one Connect. The listing's own "Copy link"
 * form; Anthropic's documented /directory/connectors/stippl redirects here.
 */
export const CLAUDE_DIRECTORY_URL = "https://claude.ai/directory/stippl";
/** Stippl's listing ("Stippl Travel Planner") in ChatGPT's plugin directory: where the ChatGPT page's add buttons go. */
export const CHATGPT_PLUGIN_URL = "https://chatgpt.com/plugins/plugin_asdk_app_6acdd5b2a8288191a1ef0bf8a5d30f02";
/** Where each assistant's add button goes. */
export const LISTING_URLS: Record<Assistant, string> = { chatgpt: CHATGPT_PLUGIN_URL, claude: CLAUDE_DIRECTORY_URL };
export const HELP_CENTER_URL = "https://stippl.crisp.help/en/article/how-do-i-use-stippl-in-chatgpt-or-claude-ifzp49/";

// ---------- hero ----------

export const HERO_CHIPS = ["japan", "thailand", "bali", "vietnam", "italy", "srilanka"] as const;
export const HERO_DEFAULT_CHIP = "thailand";

// ---------- live chat demo ----------

export const DEMO_TRIP_IDS = ["thailand", "japan", "italy"] as const;
export type DemoTripId = (typeof DEMO_TRIP_IDS)[number];

export const DEMO_TRIPS: Record<DemoTripId, { thumb: string; route: string; days: string }> = {
	thailand: { thumb: "photos/thailand.jpg", route: "img/nd-route-thailand.webp", days: "img/nd-days-thailand.webp" },
	japan: { thumb: "photos/kyoto.jpg", route: "img/nd-route-japan.webp", days: "img/nd-days-japan.webp" },
	italy: { thumb: "photos/italy.jpg", route: "img/nd-route-italy.webp", days: "img/nd-days-italy.webp" },
};

/** Every demo trip has four stops (`demo.<id>.line1` ... `line4`). */
export const DEMO_LINE_COUNT = 4;

// ---------- the "what Stippl turns it into" tiles ----------

export type PopPosition = { left?: number; right?: number; top: number };

/**
 * A pop-up next to the phone. Chips take `text` (`tile.<id>.pop<n>`); cards take `title` + `sub`
 * (`tile.<id>.pop<n>.title|sub`); the bubble takes `name` + `text`. `dot` is the chip's round marker:
 * a tick, or a flat colour. `rot` is the tilt in degrees.
 */
export type TilePop =
	| { kind: "chip"; pos: PopPosition; dot: "check" | string }
	| { kind: "avatarChip"; pos: PopPosition }
	| { kind: "card"; pos: PopPosition; rot: number; img: string }
	| { kind: "photo"; pos: PopPosition; rot: number; img: string }
	| { kind: "bubble"; pos: PopPosition; avatar: string };

export interface Tile {
	id: "route" | "days" | "onthego" | "together" | "stays" | "budget" | "packing" | "ai" | "journal" | "photobook" | "pdf";
	/** Icon badge colour: g(reen), a(mber), v(iolet), p(ink). */
	tone: "g" | "a" | "v" | "p";
	shot: string;
	/** The PDF tile shows a stack of pages instead of the phone. */
	paper?: boolean;
	/** The pop-ups, in the order they animate in. Number is the `<n>` in the i18n key. */
	pops: Array<{ n: number } & TilePop>;
}

export const TILES: Tile[] = [
	{
		id: "route",
		tone: "g",
		shot: "img/nd-route-thailand.webp",
		pops: [
			{ n: 1, kind: "chip", pos: { left: -64, top: 150 }, dot: "check" },
			{ n: 2, kind: "chip", pos: { right: -40, top: 420 }, dot: "#ea5681" },
		],
	},
	{
		id: "days",
		tone: "a",
		shot: "img/nd-days-thailand.webp",
		pops: [
			{ n: 1, kind: "card", pos: { left: -78, top: 170 }, rot: -6, img: "photos/p-grandpalace.jpg" },
			{ n: 2, kind: "card", pos: { right: -64, top: 360 }, rot: 5, img: "photos/p-chatuchak.jpg" },
		],
	},
	{
		id: "onthego",
		tone: "v",
		shot: "img/nd-onthego.webp",
		pops: [
			{ n: 1, kind: "chip", pos: { left: -92, top: 200 }, dot: "check" },
			{ n: 2, kind: "chip", pos: { right: -46, top: 400 }, dot: "#9376e8" },
		],
	},
	{
		id: "together",
		tone: "p",
		shot: "img/nd-together.webp",
		pops: [
			{ n: 1, kind: "avatarChip", pos: { left: -96, top: 180 } },
			{ n: 2, kind: "chip", pos: { right: -40, top: 430 }, dot: "check" },
		],
	},
	{
		id: "stays",
		tone: "p",
		shot: "img/nd-stays.webp",
		pops: [
			{ n: 1, kind: "card", pos: { left: -84, top: 170 }, rot: -5, img: "photos/lubd.jpg" },
			{ n: 2, kind: "chip", pos: { right: -50, top: 440 }, dot: "check" },
		],
	},
	{
		id: "budget",
		tone: "g",
		shot: "img/nd-budget.webp",
		pops: [
			{ n: 1, kind: "chip", pos: { left: -56, top: 190 }, dot: "#88E1C7" },
			{ n: 2, kind: "chip", pos: { right: -56, top: 300 }, dot: "#D4C1FF" },
			{ n: 3, kind: "chip", pos: { left: -64, top: 420 }, dot: "#FDACC3" },
		],
	},
	{
		id: "packing",
		tone: "a",
		shot: "img/nd-packing.webp",
		pops: [
			{ n: 1, kind: "chip", pos: { left: -52, top: 180 }, dot: "check" },
			{ n: 2, kind: "chip", pos: { right: -44, top: 290 }, dot: "check" },
			{ n: 3, kind: "chip", pos: { left: -60, top: 410 }, dot: "#dde9ed" },
		],
	},
	{
		id: "ai",
		tone: "v",
		shot: "img/nd-ai.webp",
		pops: [
			{ n: 1, kind: "card", pos: { left: -84, top: 190 }, rot: -5, img: "photos/p-watpho.jpg" },
			{ n: 2, kind: "chip", pos: { right: -48, top: 450 }, dot: "#9376e8" },
		],
	},
	{
		id: "journal",
		tone: "g",
		shot: "img/nd-journal.webp",
		pops: [
			{ n: 1, kind: "photo", pos: { left: -74, top: 150 }, rot: -8, img: "photos/p-longtail.jpg" },
			{ n: 2, kind: "bubble", pos: { right: -70, top: 380 }, avatar: "tiles/avatar-a.png" },
		],
	},
	{
		id: "photobook",
		tone: "a",
		shot: "img/nd-photobook.webp",
		pops: [
			{ n: 1, kind: "chip", pos: { left: -70, top: 190 }, dot: "#ffc14a" },
			{ n: 2, kind: "chip", pos: { right: -44, top: 600 }, dot: "check" },
		],
	},
	{
		id: "pdf",
		tone: "p",
		shot: "img/nd-pdf.webp",
		paper: true,
		pops: [
			{ n: 1, kind: "chip", pos: { left: -60, top: 120 }, dot: "check" },
			{ n: 2, kind: "chip", pos: { right: -40, top: 470 }, dot: "#ea5681" },
		],
	},
];

/** The three pages of the PDF tile's paper stack, back to front. */
export const PDF_PAGES = ["img/pdf-day.webp", "img/pdf-overview.webp", "img/pdf-cover.webp"] as const;
/** The four phones fanned out behind the front one. */
export const FAN_BACK_SHOTS = ["img/nd-packing.webp", "img/nd-budget.webp", "img/nd-journal.webp", "img/nd-days-thailand.webp"] as const;

export const AVATARS = ["tiles/avatar-a.png", "tiles/avatar-b.png", "tiles/avatar-c.png"] as const;

// ---------- prompt builder ----------

export const REGIONS = ["asia", "europe", "americas", "oceania", "africa"] as const;
export type Region = (typeof REGIONS)[number];
export const MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"] as const;
export const TRIP_TYPES = ["adventure", "beach", "culinary", "culture", "nature", "romantic", "offbeat", "wildlife"] as const;
export const TRIP_LENGTHS = ["weekend", "week1", "weeks2", "weeks3"] as const;
/** Default when no length is picked. */
export const DEFAULT_TRIP_LENGTH = "weeks2";
/** At most this many trip types count at once; picking another drops the oldest. */
export const MAX_TRIP_TYPES = 2;

export interface Country {
	/** Also the photo file name (countries/<id>.jpg) and the i18n key (`country.<id>.name|tag`). */
	id: string;
	region: Region;
	/** ISO code of countries/flag-<code>.png. */
	flag: string;
	badge: "mustSee" | "gem";
}

/** "Popular first": the order the designer set. */
export const COUNTRIES: Country[] = [
	{ id: "thailand", region: "asia", flag: "th", badge: "mustSee" },
	{ id: "japan", region: "asia", flag: "jp", badge: "mustSee" },
	{ id: "vietnam", region: "asia", flag: "vn", badge: "mustSee" },
	{ id: "indonesia", region: "asia", flag: "id", badge: "mustSee" },
	{ id: "srilanka", region: "asia", flag: "lk", badge: "gem" },
	{ id: "cambodia", region: "asia", flag: "kh", badge: "gem" },
	{ id: "southkorea", region: "asia", flag: "kr", badge: "mustSee" },
	{ id: "italy", region: "europe", flag: "it", badge: "mustSee" },
	{ id: "france", region: "europe", flag: "fr", badge: "mustSee" },
	{ id: "albania", region: "europe", flag: "al", badge: "gem" },
	{ id: "costarica", region: "americas", flag: "cr", badge: "mustSee" },
	{ id: "mexico", region: "americas", flag: "mx", badge: "mustSee" },
	{ id: "brazil", region: "americas", flag: "br", badge: "mustSee" },
	{ id: "australia", region: "oceania", flag: "au", badge: "mustSee" },
	{ id: "argentina", region: "americas", flag: "ar", badge: "mustSee" },
	{ id: "austria", region: "europe", flag: "at", badge: "mustSee" },
	{ id: "belgium", region: "europe", flag: "be", badge: "mustSee" },
	{ id: "bolivia", region: "americas", flag: "bo", badge: "gem" },
	{ id: "bosniaandherzegovina", region: "europe", flag: "ba", badge: "gem" },
	{ id: "bulgaria", region: "europe", flag: "bg", badge: "gem" },
	{ id: "canada", region: "americas", flag: "ca", badge: "mustSee" },
	{ id: "chile", region: "americas", flag: "cl", badge: "mustSee" },
	{ id: "china", region: "asia", flag: "cn", badge: "mustSee" },
	{ id: "colombia", region: "americas", flag: "co", badge: "mustSee" },
	{ id: "croatia", region: "europe", flag: "hr", badge: "mustSee" },
	{ id: "czechia", region: "europe", flag: "cz", badge: "mustSee" },
	{ id: "denmark", region: "europe", flag: "dk", badge: "mustSee" },
	{ id: "ecuador", region: "americas", flag: "ec", badge: "gem" },
	{ id: "egypt", region: "africa", flag: "eg", badge: "mustSee" },
	{ id: "finland", region: "europe", flag: "fi", badge: "mustSee" },
	{ id: "germany", region: "europe", flag: "de", badge: "mustSee" },
	{ id: "greece", region: "europe", flag: "gr", badge: "mustSee" },
	{ id: "guatemala", region: "americas", flag: "gt", badge: "gem" },
	{ id: "hungary", region: "europe", flag: "hu", badge: "mustSee" },
	{ id: "iceland", region: "europe", flag: "is", badge: "mustSee" },
	{ id: "india", region: "asia", flag: "in", badge: "mustSee" },
	{ id: "ireland", region: "europe", flag: "ie", badge: "mustSee" },
	{ id: "laos", region: "asia", flag: "la", badge: "gem" },
	{ id: "malaysia", region: "asia", flag: "my", badge: "mustSee" },
	{ id: "montenegro", region: "europe", flag: "me", badge: "gem" },
	{ id: "morocco", region: "africa", flag: "ma", badge: "mustSee" },
	{ id: "netherlands", region: "europe", flag: "nl", badge: "mustSee" },
	{ id: "newzealand", region: "oceania", flag: "nz", badge: "mustSee" },
	{ id: "nicaragua", region: "americas", flag: "ni", badge: "gem" },
	{ id: "norway", region: "europe", flag: "no", badge: "mustSee" },
	{ id: "panama", region: "americas", flag: "pa", badge: "gem" },
	{ id: "peru", region: "americas", flag: "pe", badge: "mustSee" },
	{ id: "philippines", region: "asia", flag: "ph", badge: "mustSee" },
	{ id: "poland", region: "europe", flag: "pl", badge: "gem" },
	{ id: "portugal", region: "europe", flag: "pt", badge: "mustSee" },
	{ id: "qatar", region: "asia", flag: "qa", badge: "gem" },
	{ id: "romania", region: "europe", flag: "ro", badge: "gem" },
	{ id: "rwanda", region: "africa", flag: "rw", badge: "gem" },
	{ id: "serbia", region: "europe", flag: "rs", badge: "gem" },
	{ id: "singapore", region: "asia", flag: "sg", badge: "mustSee" },
	{ id: "slovakia", region: "europe", flag: "sk", badge: "gem" },
	{ id: "slovenia", region: "europe", flag: "si", badge: "gem" },
	{ id: "southafrica", region: "africa", flag: "za", badge: "mustSee" },
	{ id: "spain", region: "europe", flag: "es", badge: "mustSee" },
	{ id: "sweden", region: "europe", flag: "se", badge: "mustSee" },
	{ id: "switzerland", region: "europe", flag: "ch", badge: "mustSee" },
	{ id: "taiwan", region: "asia", flag: "tw", badge: "gem" },
	{ id: "turkey", region: "europe", flag: "tr", badge: "mustSee" },
	{ id: "uae", region: "asia", flag: "ae", badge: "mustSee" },
	{ id: "unitedkingdom", region: "europe", flag: "gb", badge: "mustSee" },
	{ id: "unitedstates", region: "americas", flag: "us", badge: "mustSee" },
];

// ---------- trip ideas ----------

export const STAT_COUNT = 4;

/** `route.<id>.*` in i18n; the map image is img/card-<id>.webp. */
export const ROUTE_CARDS: Array<{ id: string; flag: string; photo: string }> = [
	{ id: "thailand", flag: "photos/flag-th.png", photo: "photos/thailand.jpg" },
	{ id: "japan", flag: "photos/flag-jp.png", photo: "photos/kyoto.jpg" },
	{ id: "italy", flag: "photos/flag-it.png", photo: "photos/italy.jpg" },
	{ id: "vietnam", flag: "photos/flag-vn.png", photo: "photos/vietnam.jpg" },
	{ id: "bali", flag: "photos/flag-id.png", photo: "photos/bali.jpg" },
	{ id: "srilanka", flag: "photos/flag-lk.png", photo: "photos/ella.jpg" },
	{ id: "portugal", flag: "countries/flag-pt.png", photo: "countries/portugal.jpg" },
	{ id: "spain", flag: "countries/flag-es.png", photo: "countries/spain.jpg" },
	{ id: "greece", flag: "countries/flag-gr.png", photo: "countries/greece.jpg" },
	{ id: "mexico", flag: "countries/flag-mx.png", photo: "countries/mexico.jpg" },
	{ id: "costarica", flag: "countries/flag-cr.png", photo: "countries/costarica.jpg" },
	{ id: "peru", flag: "countries/flag-pe.png", photo: "countries/peru.jpg" },
	{ id: "iceland", flag: "countries/flag-is.png", photo: "countries/iceland.jpg" },
	{ id: "croatia", flag: "countries/flag-hr.png", photo: "countries/croatia.jpg" },
	{ id: "morocco", flag: "countries/flag-ma.png", photo: "countries/morocco.jpg" },
	{ id: "newzealand", flag: "countries/flag-nz.png", photo: "countries/newzealand.jpg" },
];
